import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject, forkJoin } from 'rxjs';
import { tap, catchError, map, switchMap, shareReplay, finalize } from 'rxjs/operators';
import {
  Language,
  Translation,
  TranslationSet,
  TranslationUpdate,
  TranslationImportExport
} from '../models/translation.model';
import { environment } from '../../../../../apps/gs-2.0/src/environments/environment';
import { convertTwoToThreeLetterCode } from '../constants/language-codes';

@Injectable({
  providedIn: 'root'
})
export class TranslationApiService {
  private readonly apiUrl: string;
  private translationCache = new Map<string, TranslationSet>();
  private translationsSubject = new BehaviorSubject<TranslationSet | null>(null);
  private inFlightRequests = new Map<string, Observable<TranslationSet>>();

  public translations$ = this.translationsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.apiUrl = environment.apiUrl;
  }

  // Language Management
  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${this.apiUrl}/languages`).pipe(
      catchError(() => {
        // Fallback: Load from local JSON file
        return this.http.get<{languages: Language[]}>('/assets/i18n/languages.json').pipe(
          map(data => data.languages)
        );
      })
    );
  }

  getActiveLanguages(): Observable<Language[]> {
    return this.getLanguages().pipe(
      map(languages => languages.filter(lang => lang.isActive))
    );
  }

  createLanguage(language: Partial<Language>): Observable<Language> {
    return this.http.post<Language>(`${this.apiUrl}/languages`, language);
  }

  updateLanguage(id: string, language: Partial<Language>): Observable<Language> {
    return this.http.put<Language>(`${this.apiUrl}/languages/${id}`, language);
  }

  deleteLanguage(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/languages/${id}`);
  }

  // Translation Management
  getTranslations(languageCode: string, useCache = true, appType?: 'GS' | 'PORTAL' | null): Observable<TranslationSet> {
    // Create cache key with appType suffix if provided
    const cacheKey = appType ? `${languageCode}_${appType}` : languageCode;

    // Check cache first
    if (useCache && this.translationCache.has(cacheKey)) {
      const cached = this.translationCache.get(cacheKey)!;
      return of(cached);
    }

    // Check if request is already in flight
    if (this.inFlightRequests.has(cacheKey)) {
      return this.inFlightRequests.get(cacheKey)!;
    }

    // Build URL with query parameter if appType is provided
    const url = appType
      ? `${this.apiUrl}/v1/language/translations/${languageCode}?appType=${appType}`
      : `${this.apiUrl}/v1/language/translations/${languageCode}`;

    // Create shared request
    const request$ = this.http.get<any>(url).pipe(
      map(response => {
        const translationMap: { [key: string]: string } = {};

        // Handle the actual API response format: { "response": { "KEY": "VALUE", ... } }
        if (response && response.response && typeof response.response === 'object') {
          // The response.response contains the flat key-value pairs we want
          Object.assign(translationMap, response.response);
        }
        // Fallback: Handle legacy format with objects array
        else if (response && response.response && response.response.objects) {
          response.response.objects.forEach((item: any) => {
            translationMap[item.labelKey] = item.selectedLocaleValue;
          });
        }
        // Fallback: Handle array format (for backward compatibility)
        else if (response && Array.isArray(response)) {
          response.forEach((t: Translation) => {
            translationMap[t.key] = t.value;
          });
        }
        // Fallback: Handle direct object format
        else if (response && typeof response === 'object') {
          Object.assign(translationMap, response);
        }
        else {
          console.warn('Unrecognized API translation response format:', response);
        }

        return { languageCode, translations: translationMap };
      }),
      tap(translationSet => {
        this.translationCache.set(cacheKey, translationSet);
        this.translationsSubject.next(translationSet);
      }),
      catchError((error) => {
        console.warn('Failed to fetch translations from API, falling back to local JSON:', error);
        // Fallback: Load from local JSON file
        return this.loadFallbackTranslations(languageCode);
      }),
      finalize(() => {
        // Remove from in-flight requests when complete
        this.inFlightRequests.delete(cacheKey);
      }),
      shareReplay(1) // Share the result among all subscribers
    );

    // Store in-flight request
    this.inFlightRequests.set(cacheKey, request$);

    return request$;
  }

  updateTranslation(update: TranslationUpdate): Observable<Translation> {
    return this.http.put<Translation>(
      `${this.apiUrl}/translations`,
      update
    ).pipe(
      tap(() => {
        // Update cache
        if (this.translationCache.has(update.languageCode)) {
          const cached = this.translationCache.get(update.languageCode)!;
          cached.translations[update.key] = update.value;
          this.translationsSubject.next(cached);
        }
      }),
      catchError(() => {
        // For development, update local cache only
        if (this.translationCache.has(update.languageCode)) {
          const cached = this.translationCache.get(update.languageCode)!;
          cached.translations[update.key] = update.value;
          this.translationsSubject.next(cached);
        }
        return of({
          id: Date.now().toString(),
          languageId: update.languageCode,
          keyId: update.key,
          key: update.key,
          value: update.value
        } as Translation);
      })
    );
  }

  bulkUpdateTranslations(languageCode: string, translations: { [key: string]: string }): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/translations/${languageCode}/bulk`,
      { translations }
    ).pipe(
      tap(() => {
        // Update cache
        const translationSet = { languageCode, translations };
        this.translationCache.set(languageCode, translationSet);
        this.translationsSubject.next(translationSet);
      })
    );
  }

  // New API v1 endpoints for language translation management
  /**
   * Upsert a single translation using the v1 API
   * @param locale - Language code (e.g., 'eng')
   * @param translation - Translation object with key, value, and description
   */
  upsertLanguageTranslation(
    locale: string,
    translation: { key: string; value: string; description?: string }
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/v1/language/${locale}`,
      translation
    ).pipe(
      tap(() => {
        // Update cache
        if (this.translationCache.has(locale)) {
          const cached = this.translationCache.get(locale)!;
          cached.translations[translation.key] = translation.value;
          this.translationsSubject.next(cached);
        }
      })
    );
  }

  /**
   * Bulk upsert multiple translations using the v1 API
   * @param locale - Language code (e.g., 'eng')
   * @param items - Array of translation objects with key, value, description, and appType
   */
  bulkUpsertLanguageTranslations(
    locale: string,
    items: Array<{ key: string; value: string; description?: string; appType?: string }>
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/v1/language/bulk/${locale}`,
      { items }
    ).pipe(
      tap(() => {
        // Update cache with all bulk-updated translations
        if (this.translationCache.has(locale)) {
          const cached = this.translationCache.get(locale)!;
          items.forEach(item => {
            cached.translations[item.key] = item.value;
          });
          this.translationsSubject.next(cached);
        } else {
          // Create new cache entry
          const translations: { [key: string]: string } = {};
          items.forEach(item => {
            translations[item.key] = item.value;
          });
          const translationSet = { languageCode: locale, translations };
          this.translationCache.set(locale, translationSet);
          this.translationsSubject.next(translationSet);
        }
      })
    );
  }

  // Translation Keys Management
  getTranslationKeys(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/translation-keys`).pipe(
      catchError(() => {
        // Fallback: Load keys from English translation file (using 3-letter code)
        return this.http.get<{[key: string]: string}>('/assets/i18n/eng.json').pipe(
          map(translations => Object.keys(translations))
        );
      })
    );
  }

  addTranslationKey(key: string, defaultValue: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/translation-keys`, {
      key,
      defaultValue
    });
  }

  // Import/Export
  exportTranslations(languageCode: string): Observable<TranslationImportExport> {
    // Get all keys and current translations, then merge them
    return forkJoin({
      allKeys: this.getTranslationKeys(),
      currentTranslations: this.getTranslations(languageCode)
    }).pipe(
      map(({ allKeys, currentTranslations }) => ({
        language: languageCode,
        translations: allKeys.map(key => ({
          key,
          value: currentTranslations.translations[key] || '',
          category: key.split('.')[0]
        }))
      }))
    );
  }

  importTranslations(data: TranslationImportExport): Observable<void> {
    const translations: { [key: string]: string } = {};
    data.translations.forEach(t => {
      translations[t.key] = t.value;
    });
    return this.bulkUpdateTranslations(data.language, translations);
  }

  // Clear cache
  clearCache(languageCode?: string): void {
    if (languageCode) {
      this.translationCache.delete(languageCode);
    } else {
      this.translationCache.clear();
    }
  }

  // Load fallback translations from JSON files
  private loadFallbackTranslations(languageCode: string): Observable<TranslationSet> {
    // Convert to 3-letter code for file lookup (e.g., 'en' -> 'eng')
    const iso639_3Code = convertTwoToThreeLetterCode(languageCode);
    const fallbackPath = `/assets/i18n/${iso639_3Code}.json`;

    return this.http.get<{[key: string]: string}>(fallbackPath).pipe(
      map(translations => {
        const translationSet = { languageCode, translations };
        this.translationCache.set(languageCode, translationSet);
        this.translationsSubject.next(translationSet);
        return translationSet;
      }),
      catchError(() => {
        // If specific language file doesn't exist, try English as ultimate fallback
        if (languageCode !== 'en' && languageCode !== 'eng') {
          return this.http.get<{[key: string]: string}>('/assets/i18n/eng.json').pipe(
            map(translations => {
              const translationSet = { languageCode, translations };
              this.translationCache.set(languageCode, translationSet);
              this.translationsSubject.next(translationSet);
              return translationSet;
            })
          );
        }
        // Return empty translations if all fails
        const emptySet = { languageCode, translations: {} };
        return of(emptySet);
      })
    );
  }
}
